/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {MaterialPositionTableItemModel} from './material-position-table-item.model';
import {ClaimPositionDataModel} from '../../models/claim-position-data.model';
import {WarningsEnum} from '../../../shared/warnings/warnings.enum';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {MaterialPositionModalComponent} from '../material-position-modal/material-position-modal.component';
import {ClaimErrorBaseModel} from '../../../api/ms-claims/contracts/claim-error-base.model';
import {DecisionBaseModel} from '../../../api/ms-claims/contracts/decision-base.model';
import {BusinessCategoryBaseModel} from '../../../api/ms-claims/contracts/business-category-base.model';
import {forkJoin, Subscription} from 'rxjs';
import {MaterialPositionTableService} from './service/material-position-table.service';
import {
    StepPositionDetailMaterialModel
} from '../../claim-create-new/step-position-detail/step-position-detail-material.model';
import {ClaimPositionDetailModel} from '../../../api/ms-claims/contracts/claim-position-detail.model';
import {Router} from '@angular/router';
import {ReferenceDataApi} from '../../../api/ms-claims/reference-data.api';
import {TooltipAlignmentEnum} from '../../../shared/tooltip/tooltip-alignment.enum';
import {TooltipEnum} from '../../../shared/tooltip/tooltip.enum';
import {SeverityEnum} from '../../../api/generic/contracts/severity.enum';
import {HandlingUnitValidationModel} from '../../../api/ms-claims/contracts/handling-unit-validation.model';
import {HandlingUnitApi} from '../../../api/ms-claims/handling-unit.api';
import {ClaimInformationMandatoryFieldsEnum} from '../claim-information-form/claim-information-mandatory-fields.enum';
import trackByIndex from '../../../shared/utils/trackByIndex';
import {ToastWrapperService} from '../../../shared/utils/toast-wrapper-service/toast-wrapper-service';
import {ClaimsApi} from '../../../api/ms-claims/claims.api';
import {MaterialPositionErrorCodeEnum} from '../material-position-modal/models/material-position-error-code.enum';

@Component({
    selector: 'app-material-position-table',
    templateUrl: './material-position-table.component.html',
    styleUrls: ['./material-position-table.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MaterialPositionTableComponent implements OnInit, OnDestroy, OnChanges {

    /** Table data variables **/
    @Output() isValidForSave = new EventEmitter<boolean>();
    @Input() material: StepPositionDetailMaterialModel;
    @Input() positions: ClaimPositionDetailModel[] = [];
    @Input() claimNumber: string;
    @Input() maxNumberOfPositions: number;
    /** Table UI variables **/
    @Input() showSectionTitle = false;
    @Input() showMaterialTitle = true;
    @Input() showButtonAdd = true;
    @Input() showContextMenu = true;
    @Input() disableHandlingUnitTooltips = false;
    @Input() maxElements: number;
    @Input() clearPositionTable = true;
    @Input() selectAll = false;
    @Input() disableTableButtons = false;
    @Input() editMode = false;
    /** Loading configuration variables */
    @Input() enableComponentLoading = false;
    @Input() enableTableLoading = false;
    @Output() isLoading = new EventEmitter<boolean>();
    /** Decision codes variables */
    @Input() partnerCode: string;
    @Input() supplyingPackingPlantCode: string;
    @Input() claimCodeCode: string;
    /** Modal instances variables **/
    @Input() errorCodes: ClaimErrorBaseModel[] = [];
    @Input() decisionCodes: DecisionBaseModel[] = [];
    @Input() businessCategories: BusinessCategoryBaseModel[] = [];
    /** Dependent fields parents error map */
    @Input() dependentFieldParentsErrors = new EventEmitter<Map<ClaimInformationMandatoryFieldsEnum, TooltipEnum>>();
    @Input() dependentFieldParentsErrorsMap = new Map<ClaimInformationMandatoryFieldsEnum, TooltipEnum>();

    @ViewChild('tableRef') tableRef: ElementRef;

    /** Table data variables **/
    public positionItems: MaterialPositionTableItemModel[] = [];
    /** Loading configuration variables */
    public tableIsLoading: boolean;
    public requiredForSendLabel = 'Required for send';

    public WarningsEnum = WarningsEnum;

    public SeverityEnum = SeverityEnum;
    /** Dependent fields parents error map */
    public mandatoryDecisionCodeParentFieldWithErrors: ClaimInformationMandatoryFieldsEnum;
    public mandatoryHandlingUnitParentFieldWithErrors: ClaimInformationMandatoryFieldsEnum;
    ClaimInformationMandatoryFieldsEnum = ClaimInformationMandatoryFieldsEnum;

    public subscriptions = new Subscription();

    TooltipAlignmentEnum = TooltipAlignmentEnum;
    TooltipEnum = TooltipEnum;
    public readonly trackByIndex = trackByIndex;

    constructor(private modalService: NgbModal,
                private positionService: MaterialPositionTableService,
                private toast: ToastWrapperService,
                private router: Router,
                private claimReferenceApi: ReferenceDataApi,
                private handlingUnitApi: HandlingUnitApi,
                private changeDetector: ChangeDetectorRef,
                private claimsApi: ClaimsApi) {
    }

    get tableElement(): Element {
        return this.tableRef.nativeElement;
    }

    ngOnInit(): void {
        /** Start table loading, if enabled */
        this.tableIsLoading = this.enableComponentLoading;

        this.getDropdownIndependentData();

        /** Subscription to set the claim information dependent errors */
        this.subscriptions.add(this.dependentFieldParentsErrors
                .subscribe((errors: Map<ClaimInformationMandatoryFieldsEnum, TooltipEnum>) => {
                    this.dependentFieldParentsErrorsMap = errors;
                    this.manageMandatoryParentFieldErrors();
                }));

        /** Subscription to populate table */
        this.subscriptions.add(this.positionService.positionData.subscribe(positions => {
            this.positions = positions;
            this.positionItems = this.mapPositionItems(this.positions);
            this.changeDetector.detectChanges();
            this.isValidForSave.emit(this.positionItems.filter(position =>
                    position.detail.handlingUnitValidation && position.detail.handlingUnitValidation.severity === SeverityEnum.ERROR
                    || !this.decisionIsValid(position)).length === 0);
        }));

        /** Refresh position data in service with input variable **/
        this.positionService.refreshPositionData(this.positions);
    }

    get colspan(): number {
        return this.editMode ? 8 : 7;
    }

    /**
     * Retrieves the independent data options from the BE (error code and BI categories)
     */
    getDropdownIndependentData(): void {
        if (this.editMode) {
            this.tableIsLoading = true;
            this.isLoading.emit(true);
            forkJoin([
                this.claimReferenceApi.getActiveErrors(),
                this.claimReferenceApi.getActiveBiCategories(),
            ]).subscribe((([errorCodes, businessCategories]:
                                   [ClaimErrorBaseModel[],
                                       BusinessCategoryBaseModel[]]): void => {
                this.errorCodes = errorCodes;
                this.businessCategories = businessCategories;
            })).add(() => {
                this.tableIsLoading = false;
                this.isLoading.emit(false);
            });
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['positions'] && !changes['positions'].firstChange) {
            this.positionItems = this.mapPositionItems(changes['positions'].currentValue);
            /** Stops table loading after receiving the position list */
            this.tableIsLoading = false;
        }

        if (changes['material'] && !changes['material'].firstChange && this.clearPositionTable) {
            this.material = changes['material'].currentValue;
            this.handlingUnitBulkValidation();
        }

        if (changes['partnerCode'] || changes['supplyingPackingPlantCode']) {
            this.handlingUnitBulkValidation();
        }

        if (changes['partnerCode'] || changes['supplyingPackingPlantCode'] || changes['claimCodeCode']) {
            this.getDecisionCodes();
        }
    }

    /**
     * Runs the HU bulk validation
     */
    handlingUnitBulkValidation(): void {
        if (this.material != null && this.material.number != null && this.supplyingPackingPlantCode != null && this.partnerCode != null) {
            const handlingUnits: string[] = [];
            this.positionItems.forEach(position => {
                if (position.detail.handlingUnit != null) {
                    handlingUnits.push(position.detail.handlingUnit);
                }
            });
            const validationModel: HandlingUnitValidationModel = {
                materialNumber: this.material.number,
                partnerCode: this.partnerCode,
                supplyingPackingPlantCode: this.supplyingPackingPlantCode,
                handlingUnitNumbers: handlingUnits
            };

            if (handlingUnits.length > 0) {
                this.isLoading.emit(true);
                this.subscriptions.add(
                        this.handlingUnitApi.validateHandlingUnits(validationModel).subscribe(result => {
                            this.positionItems.forEach(position => {
                                const huValidation = result.find(item =>
                                        item.handlingUnitNumber === position.detail.handlingUnit);
                                if (huValidation != null) {
                                    position.detail.handlingUnitValidation = huValidation.message;
                                }
                            });
                        }).add(() => {
                            this.positionService.refreshPositionTableItemData(this.positionItems);
                            this.isLoading.emit(false);
                        }));
            }
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    /** Maps positions from {@link ClaimPositionDataModel} to {@link MaterialPositionTableItemModel} **/
    mapPositionItems(positions: ClaimPositionDetailModel[]): MaterialPositionTableItemModel[] {
        return positions.map((position) => ({
            detail: position,
            overlayInProgress: false,
            selected: false,
            selectionDisabled: false
        }));
    }

    /** Determines if the view more link should be displayed */
    showViewMore(): boolean {
        return this.claimNumber && this.positionItems.length > this.maxElements;
    }

    /** Creates the view more link path */
    viewMore(): void {
        this.router.navigate(['claim', this.claimNumber]);
    }

    canEdit(position: ClaimPositionDetailModel): boolean {
        return true;
    }

    canDelete(position: ClaimPositionDetailModel): boolean {
        return true;
    }

    /** Adds a position to the position list **/
    addPosition = (): void => {
        const modalRef = this.modalService.open(MaterialPositionModalComponent, {centered: true});
        modalRef.componentInstance.material = this.material;
        modalRef.componentInstance.businessCategoryOptions = this.businessCategories;
        modalRef.componentInstance.errorCodeOptions = this.errorCodes;
        modalRef.componentInstance.decisionCodeOptions = this.decisionCodes;
        modalRef.componentInstance.supplyingPackingPlantCode = this.supplyingPackingPlantCode;
        modalRef.componentInstance.partnerCode = this.partnerCode;
        modalRef.componentInstance.numberOfPositions = this.positionItems.length;
        modalRef.componentInstance.maxNumberOfPositions = this.maxNumberOfPositions;
        modalRef.componentInstance.excludedDecisionCodes = this.getExcludedDecisionCodes();

        this.subscriptions.add(
                modalRef.componentInstance.addClick.subscribe((position: ClaimPositionDataModel) => {
                    position.decisionIsValid = true;
                    const newPosition: ClaimPositionDataModel = position;
                    this.addPositionItems([newPosition]);
                    modalRef.componentInstance.numberOfPositions = this.positionItems.length;
                    modalRef.componentInstance.excludedDecisionCodes = this.getExcludedDecisionCodes();
                    this.undoAll();
                    this.toast.success(`Position <strong>added</strong> successfully`, 'Success');
                })
        );
    };

    addPositionItems(positionItems: ClaimPositionDataModel[]): void {
        if (positionItems == null || positionItems.length === 0) {
            return;
        }

        positionItems.forEach((position: ClaimPositionDataModel) => {
            const element: MaterialPositionTableItemModel = {
                detail: position,
                overlayInProgress: false,
                selected: false,
                selectionDisabled: false
            };

            // Add to list
            this.positionItems.push(element);
        });
        // Update position service
        this.positionService.refreshPositionTableItemData(this.positionItems);
    }

    edit = (position: MaterialPositionTableItemModel): void => {
        const modalRef = this.modalService.open(MaterialPositionModalComponent, {centered: true});
        modalRef.componentInstance.selectedPosition = position.detail;
        modalRef.componentInstance.material = this.material;
        modalRef.componentInstance.businessCategoryOptions = this.businessCategories;
        modalRef.componentInstance.errorCodeOptions = this.errorCodes;
        modalRef.componentInstance.decisionCodeOptions = this.decisionCodes;
        modalRef.componentInstance.supplyingPackingPlantCode = this.supplyingPackingPlantCode;
        modalRef.componentInstance.partnerCode = this.partnerCode;
        modalRef.componentInstance.claimCodeCode = this.claimCodeCode;
        modalRef.componentInstance.numberOfPositions = this.positionItems.length;
        modalRef.componentInstance.maxNumberOfPositions = this.maxNumberOfPositions;
        modalRef.componentInstance.excludedDecisionCodes = this.getExcludedDecisionCodes(position);
        modalRef.componentInstance.addClick.subscribe((editedPosition: ClaimPositionDataModel) => {
            this.positionItems[this.positionItems.indexOf(position)] = {
                detail: editedPosition,
                overlayInProgress: false,
                selected: position.selected,
                selectionDisabled: position.selectionDisabled
            };

            modalRef.componentInstance.excludedDecisionCodes = this.getExcludedDecisionCodes(position);
            this.positionService.refreshPositionTableItemData(this.positionItems);
            this.toast.success(`Position <strong>updated</strong> successfully`, 'Update success');
        });
    };

    /**
     * Removes a position overlay
     * @param position The Position to remove the overlay
     * */
    cancelOverlayInProgress(position: MaterialPositionTableItemModel): void {
        this.undoAll();
    }

    /**
     * Deletes a position from the material positon table
     * @param index The index of the position to delete
     * */
    delete(index: number): void {
        const [deletedPosition]: MaterialPositionTableItemModel[] = this.positionItems.splice(index, 1);
        this.positionService.refreshPositionTableItemData(this.positionItems);
        this.toast.success(
                `Position <strong>deleted</strong> successfully`,
                'Success');
    }

    /**
     * Sets a position overlay to true
     * @param position The position to apply the overlay
     * */
    overlayInProgress(position: MaterialPositionTableItemModel): void {
        position.overlayInProgress = true;
        this.disabledAllSelections();
    }

    undoAll(): any {
        // unselect all
        this.selectAll = false;
        // unselect all positions
        this.positionItems.forEach(position => {
            position.overlayInProgress = false;
            position.selected = false;
            position.selectionDisabled = false;
        });
    }

    /**
     * Retrieves the decision codes dropdown options from the BE
     */
    getDecisionCodes(): void {
        if (this.partnerCode != null
                && this.supplyingPackingPlantCode != null
                && this.claimCodeCode != null) {
            this.tableIsLoading = true;
            this.isLoading.emit(true);
            this.subscriptions.add(
                    this.claimReferenceApi.getActiveDecisionCodes(
                            this.partnerCode,
                            this.supplyingPackingPlantCode,
                            this.claimCodeCode)
                            .subscribe((decisions) => {
                                this.decisionCodes = decisions;
                                this.validateDecisionCodes();
                            })
                            .add(() => {
                                this.tableIsLoading = false;
                                this.isLoading.emit(false);
                            }));
        }
    }

    /**
     * Validates the position decision codes
     */
    validateDecisionCodes(): void {
        this.positionItems.forEach(position => {
            if (position.detail.decision != null && position.detail.decision.code != null) {
                position.detail.decisionIsValid = this.decisionCodes.some(
                        decision => position.detail.decision.code === decision.code);
            } else {
                position.detail.decisionIsValid = true;
            }
        });
        this.positionService.refreshPositionTableItemData(this.positionItems);
    }

    /**
     * Manages the mandatory mandatory parent field errors.
     * Sets the {@link mandatoryDecisionCodeParentFieldWithErrors} and {@link mandatoryHandlingUnitParentFieldWithErrors} values.
     * If errors exist, sets the {@link disableTableButtons} as true to disable the table buttons
     */
    manageMandatoryParentFieldErrors(): void {
        this.mandatoryDecisionCodeParentFieldWithErrors = null;
        this.mandatoryHandlingUnitParentFieldWithErrors = null;

        if (this.dependentFieldParentsErrorsMap.get(ClaimInformationMandatoryFieldsEnum.CLAIM_CODE)) {
            this.mandatoryDecisionCodeParentFieldWithErrors = ClaimInformationMandatoryFieldsEnum.CLAIM_CODE;
        }

        if (this.dependentFieldParentsErrorsMap.get(ClaimInformationMandatoryFieldsEnum.MATERIAL)) {
            this.mandatoryHandlingUnitParentFieldWithErrors = ClaimInformationMandatoryFieldsEnum.MATERIAL;
        }

        if (this.dependentFieldParentsErrorsMap.get(ClaimInformationMandatoryFieldsEnum.PARTNER)) {
            this.mandatoryDecisionCodeParentFieldWithErrors = ClaimInformationMandatoryFieldsEnum.PARTNER;
            this.mandatoryHandlingUnitParentFieldWithErrors = ClaimInformationMandatoryFieldsEnum.PARTNER;
        } else if (this.dependentFieldParentsErrorsMap.get((ClaimInformationMandatoryFieldsEnum.PLANT))) {
            this.mandatoryDecisionCodeParentFieldWithErrors = ClaimInformationMandatoryFieldsEnum.PLANT;
            this.mandatoryHandlingUnitParentFieldWithErrors = ClaimInformationMandatoryFieldsEnum.PLANT;
        }

        this.disableTableButtons = this.mandatoryHandlingUnitParentFieldWithErrors != null ||
                this.mandatoryDecisionCodeParentFieldWithErrors != null;

        if (this.disableTableButtons) {
            this.selectAll = false;
            this.positionItems.forEach(position => position.selected = false);
        }
    }

    /**
     * Determines if a field is invalid
     * @return boolean if the field is invalid
     */
    invalidParentField(parentField: ClaimInformationMandatoryFieldsEnum): boolean {
        const errors = this.dependentFieldParentsErrorsMap.get(parentField);
        return errors != null && errors === TooltipEnum.ERROR;
    }

    /**
     * Determines if the dependent field tooltip should be disabled
     * @param parentFieldWithError The directly dependent parent field with error
     * @param fieldIsValid If the field is valid
     * @returns boolean If the dependent field tooltip should be disabled
     */
    disableDependentFieldTooltip(fieldIsValid: boolean, parentFieldWithError: ClaimInformationMandatoryFieldsEnum): boolean {
        return fieldIsValid && parentFieldWithError == null && !this.invalidParentField(parentFieldWithError);
    }

    /**
     * Determines if the dependent field should have error text style
     * @param parentFieldWithErrors The directly dependent parent field with errors
     * @param fieldIsValid If the field is valid
     */
    dependentFieldErrorStyle(fieldIsValid: boolean, parentFieldWithErrors: ClaimInformationMandatoryFieldsEnum): boolean {
        if (parentFieldWithErrors != null) {
            return this.dependentFieldParentsErrorsMap.get(parentFieldWithErrors) === TooltipEnum.ERROR;
        } else {
            return !fieldIsValid;
        }
    }

    /**
     * Determines a dependent field tooltip type
     * @return TooltipEnum the dependent field code tooltip type
     */
    determineDependentFieldTooltipType(parentField: ClaimInformationMandatoryFieldsEnum): TooltipEnum {
        const tooltipType = this.dependentFieldParentsErrorsMap.get(parentField);
        if (tooltipType) {
            return tooltipType;
        }
        return TooltipEnum.ERROR;
    }

    /**
     * Check if the decision in a certain position item is valid
     * @param position The position to check
     * @returns boolean If the decision is valid or not
     */
    decisionIsValid(position: MaterialPositionTableItemModel): boolean {
        return position.detail.decisionIsValid == null || position.detail.decisionIsValid;
    }

    /**
     * Check if the handling in a certain position item is valid or not validated
     * @param position The position to check
     * @returns boolean If the handling unit is valid or not validated or not
     */
    handlingUnitIsValid(position: ClaimPositionDetailModel): boolean {
        return position.handlingUnitValidation == null || position.handlingUnitValidation.severity !== SeverityEnum.ERROR;
    }

    /**
     * Determines the handling unit tooltip type
     * @param position The position handling unit to check
     * @return TooltipEnum The handling unit tooltip type
     */
    handlingUnitTooltipType(position: ClaimPositionDetailModel): TooltipEnum {
        if (this.mandatoryHandlingUnitParentFieldWithErrors != null) {
            return this.determineDependentFieldTooltipType(this.mandatoryHandlingUnitParentFieldWithErrors);
        } else if (position.handlingUnitValidation) {
            switch (position.handlingUnitValidation.severity) {
                case SeverityEnum.ERROR:
                    return TooltipEnum.ERROR;
                    break;
                case SeverityEnum.INFO:
                    return TooltipEnum.INFORMATION;
                    break;
                case SeverityEnum.WARNING:
                    return TooltipEnum.WARNING;
                    break;
                default:
                    return TooltipEnum.NONE;
            }
        } else {
            return TooltipEnum.NONE;
        }
    }

    disableAddPosition(): boolean {
        return !this.tableIsLoading
                && this.disableTableButtons
                || this.positionItems.length >= this.maxNumberOfPositions;
    }

    getExcludedDecisionCodes(position?: MaterialPositionTableItemModel): DecisionBaseModel[] {
        return this.positionItems.filter(p => p.detail.decision != null && p !== position)
                .map(p => p.detail.decision.excludedDecisions)
                .reduce((acc, val) => acc.concat(val), [])
                .filter((excluded, index, array) => excluded != null && array.indexOf(excluded) === index);
    }

    showTimeEffortRequiredForSend(position: MaterialPositionTableItemModel): boolean {
        return position.detail.decision ? position.detail.decision.hasTimeEffort && position.detail.timeEffort === null : false;
    }

    togglePosition(selectedPosition: MaterialPositionTableItemModel): void {
        // Toggle selectedPosition
        selectedPosition.selected = !selectedPosition.selected;
        // select all if all positions are selected
        this.selectAll = this.positionItems.every(position => position.selected);
    }

    toggleAll(): void {
        // toggle select all invert
        this.selectAll = !this.selectAll;
        // Select or unselect all positions
        this.selectAll ?
                this.positionItems.forEach(position => position.selected = true) :
                this.positionItems.forEach(position => position.selected = false);
    }

    showSelections(): boolean {
        return this.editMode;
    }

    disabledSelectAll(): boolean {
        return this.disableTableButtons ||
                (this.editMode && (this.positionItems.length === 0
                        || this.positionItems.some(position => position.overlayInProgress)));
    }

    showIntermediate(): boolean {
        return this.positionItems.some(position => position.selected) &&
                !this.positionItems.every(position => position.selected);
    }

    disabledLinkButton(): boolean {
        return this.disableTableButtons ||
                (this.editMode && this.positionItems.every(position => !position.selected));
    }

    clearPositions(): void {
        if (this.editMode && this.positionItems.some(position => position.selected)) {
            this.positionItems = this.positionItems.filter(position => !position.selected);
            this.positionService.refreshPositionTableItemData(this.positionItems);
            this.selectAll = false;
            this.toast.success(`Positions <strong>deleted</strong> successfully`, 'Success');
        }
    }

    getErrorMessage(code: string, handlingUnit: string): string {
        switch (code) {
            case MaterialPositionErrorCodeEnum['INVALID_HU_NOT_FOUND_SUPPLYING_PACKING_PLANT']:
                return `HU <strong>${handlingUnit}</strong> with material ${this.material.number} 
<strong>was not found</strong> in the selected supplying packing plant ${this.supplyingPackingPlantCode}.`;
                break;
            case MaterialPositionErrorCodeEnum['INVALID_HU_NOT_FOUND_PARTNER']:
                return `HU ${handlingUnit} <strong>is not associated</strong> to the partner ${this.partnerCode}.`;
                break;
            default:
                break;
        }
    }

    private disabledAllSelections(): void {
        // Unselect all
        this.selectAll = false;
        // Unselect all positions
        this.positionItems.forEach(position => {
            position.selected = false;
            position.selectionDisabled = true;
        });
    }
}

